<template>
  <v-card outlined>
    <v-card-title class="primary--text">{{
      $t("contratos.documentos")
    }}</v-card-title>
    <v-divider></v-divider>
    <v-simple-table>
      <thead>
        <tr>
          <th>{{ $t("contratos.tipo") }}</th>
          <th>{{ $t("contratos.nombre") }}</th>
          <th>{{ $t("contratos.fecha") }}</th>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr :key="doc.REF" v-for="doc in docs">
          <td>{{ doc.tipo }}</td>
          <td>{{ doc.NOMBRE }}</td>
          <td>{{ parseDate(doc.FECHA, false, true, true) }}</td>
          <td>
            <div class="d-flex flex-wrap align-center" style="gap: 4px">
              <audio v-if="isAudio(doc.NOMBRE)" controls :src="doc.URL"></audio>

              <v-btn @click="download(doc)" rounded color="primary" small>
                <v-icon left>mdi-download</v-icon>
                {{ $t("common.descargar") }}
              </v-btn>
            </div>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <v-divider></v-divider>
    <div class="pa-2">
      <v-dialog max-width="800">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" rounded v-on="on" v-bind="attrs">
            <v-icon left>mdi-upload</v-icon>
            {{ $t("common.subir") }}</v-btn
          >
        </template>

        <v-card>
          <v-card-title>Sube aquí los archivos</v-card-title>
          <DragAndDrop v-model="documento" />
          <v-divider />
          <div class="pa-2 d-flex">
            <v-spacer />
            <v-btn color="primary" @click.stop="enviar">Enviar</v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- CAMBIAR ESTADO POR "PDT. AUDIO" -->
      <v-dialog max-width="800" v-if="estado === 'PDT. VERIFICACION'">
        <template v-slot:activator="{ on, attrs }">
          <v-btn color="primary" rounded v-on="on" v-bind="attrs">
            <v-icon left>mdi-volume-high</v-icon>
            {{ $t("common.subirAudio") }}
          </v-btn>
        </template>

        <v-card>
          <v-card-title>Sube aquí el audio</v-card-title>
          <AudioDragAndDrop
            v-model="archivoAudio"
            @file-invalid="handleInvalidAudio"
          />
          <v-divider />
          <div class="pa-2 d-flex">
            <v-spacer />
            <v-btn color="primary" @click.stop="enviarAudio">Enviar</v-btn>
          </div>
        </v-card>
      </v-dialog>
    </div>
  </v-card>
</template>

<script>
import { parseDate, downloadFile } from "@/utils/index.js";
import EstadoContrato from "./EstadoContrato.vue";

export default {
  components: {
    DragAndDrop: () => import("@/components/DragAndDrop.vue"),
    AudioDragAndDrop: () =>
      import("@/modules/audios/components/DragAndDrop.vue"),
  },
  props: {
    docs: Array,
    refcontrato: Number,
    estado: String,
  },
  data() {
    return {
      documento: null,
      archivoAudio: null,
    };
  },
  methods: {
    handleInvalidAudio(info) {
      if (Array.isArray(info)) {
        for (const i of info) {
          this._showAudioError(i);
        }
      } else {
        this._showAudioError(info);
      }
    },

    parseDate,
    isAudio(name) {
      const audioRegex = /\.(mp3|wav|ogg|aac|m4a)$/i;
      return audioRegex.test(name);
    },
    async download(doc) {
      let { data } = await axios({
        url: `/contratos/documento/${doc.ARCHIVO}`,
        responseType: "blob",
      });
      let ext = doc.ARCHIVO.split(".")[doc.ARCHIVO.split(".").length - 1];
      downloadFile(new File([data], `${doc.NOMBRE}.${ext}`));
    },

    validarDuracionAudio(archivo) {
      return new Promise((resolve, reject) => {
        const audio = document.createElement("audio");
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
          window.URL.revokeObjectURL(audio.src);
          const duracion = audio.duration;

          if (duracion < 5) {
            reject("El audio es demasiado corto.");
          } else {
            resolve(duracion);
          }
        };

        audio.onerror = () => {
          reject("No se pudo cargar el archivo de audio.");
        };

        audio.src = URL.createObjectURL(archivo);
      });
    },
    _showAudioError({ file, reason, duration }) {
      if (reason === "too-short") {
        this.$root.$emit(
          "snack",
          `El archivo "${file.name}" es muy corto (${duration.toFixed(
            1
          )}s). Duración mínima: 5s.`
        );
      } else if (reason === "not-audio") {
        this.$root.$emit(
          "snack",
          `El archivo "${file.name}" no es un audio válido.`
        );
      } else {
        this.$root.$emit(
          "snack",
          `Error al procesar el archivo "${file.name}".`
        );
      }
    },

    async enviar() {
      try {
        if (!this.documento) {
          this.$root.$emit("snack", this.$t("documentacion.error_subir"));
          return;
        }

        // Verificación de extensión
        const allowedExtensions = [
          "pdf",
          "doc",
          "docx",
          "xls",
          "xlsx",
          "png",
          "jpg",
          "jpeg",
        ];
        const extension = this.documento.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(extension)) {
          this.$root.$emit(
            "snack",
            this.$t("documentacion.error_extension") ||
              `Extensión no permitida: .${extension}`
          );
          return;
        }

        const formData = new FormData();
        formData.append("file", this.documento);

        await axios.post(`/contratos/documento/${this.refcontrato}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        this.$emit("uploaded");
      } catch {
        this.$root.$emit("snack", this.$t("documentacion.error_subir"));
      }
    },

    async enviarAudio() {
      try {
        if (!this.archivoAudio || this.archivoAudio.length === 0) {
          this.$root.$emit("snack", this.$t("documentacion.error_subir"));
          return;
        }

        // Validar que todos los archivos sean de tipo audio
        for (const file of this.archivoAudio) {
          if (!file.type.startsWith("audio/")) {
            this.$root.$emit(
              "snack",
              this.$t("documentacion.error_formato_audio")
            );
            return;
          }
        }

        const formData = new FormData();
        this.archivoAudio.forEach((file) => formData.append("file", file));
        formData.append("tipo", "AUDIO");

        await axios.post(`/contratos/documento/${this.refcontrato}`, formData);

        this.$emit("uploaded");
      } catch (e) {
        this.$root.$emit("snack", this.$t("documentacion.error_subir"));
      }
    },
  },
};
</script>
